import { NodeSelection } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'

const EDGE_MARGIN = 5

type CaretScrollInput = {
  scrollY: number
  caretTop: number
  caretBottom: number
  /** Bottom edge of the visible viewport (visual viewport offset + height) */
  viewportHeight: number
  maxScroll: number
  /** Height at the top of the viewport covered by sticky or fixed headers */
  topInset: number
  /** Height at the bottom of the viewport covered by fixed chrome */
  bottomInset: number
}

/**
 * Where the page should scroll to for the caret to be visible: null (stay put) while it is inside the area the fixed
 * chrome leaves free, otherwise the smallest move that brings it back just inside the nearer edge. Caret rects are
 * viewport coordinates. A caret is never centred: a click or an arrow key must not move the line the user is on.
 */
export function caretRevealScrollTarget({
  scrollY,
  caretTop,
  caretBottom,
  viewportHeight,
  maxScroll,
  topInset,
  bottomInset
}: CaretScrollInput) {
  const bandTop = topInset + EDGE_MARGIN
  const bandBottom = viewportHeight - bottomInset - EDGE_MARGIN
  // The chrome leaves no room for a line: nothing can keep the caret visible, so do not fight it
  if (bandBottom - bandTop < caretBottom - caretTop) return null
  let delta: number
  if (caretTop < bandTop) delta = caretTop - bandTop
  else if (caretBottom > bandBottom) delta = caretBottom - bandBottom
  else return null
  const target = Math.min(Math.max(scrollY + delta, 0), maxScroll)
  return Math.abs(target - scrollY) < 1 ? null : target
}

const PAGE_OVERLAP = 24

type PageMoveInput = {
  scrollY: number
  /** Caret top and bottom in viewport coordinates */
  caretTop: number
  caretBottom: number
  viewportHeight: number
  /** Height at the top covered by a sticky or fixed header */
  topInset: number
  /** Height at the bottom covered by fixed chrome */
  bottomInset: number
  maxScroll: number
  direction: 'up' | 'down'
}

/**
 * Page Up / Page Down as one move: scroll by what is visible (less one line of overlap) and move the caret by the
 * same distance, so it keeps its place on screen. When the scroll hits the end of the page the caret takes the rest
 * of the distance; with nothing left to scroll `atEdge` tells the caller to go to the first or last line. The caret is
 * always kept inside the visible area, never under the chrome.
 */
export function pageMoveTarget({
  scrollY,
  caretTop,
  caretBottom,
  viewportHeight,
  topInset,
  bottomInset,
  maxScroll,
  direction
}: PageMoveInput) {
  const sign = direction === 'down' ? 1 : -1
  const distance = Math.max(viewportHeight - topInset - bottomInset - PAGE_OVERLAP, 0)
  const scrollTo = Math.min(Math.max(scrollY + sign * distance, 0), maxScroll)
  const scrolled = Math.abs(scrollTo - scrollY)
  // The whole line stays above the bottom edge, so a probe in its middle still hits the page
  const lowest = viewportHeight - bottomInset - EDGE_MARGIN - (caretBottom - caretTop)
  const caretY = Math.min(Math.max(caretTop + sign * (distance - scrolled), topInset + EDGE_MARGIN), lowest)
  return { scrollTo, caretY, atEdge: scrolled < 1 }
}

/** True when the editor sits in something that scrolls or floats on its own (modal, menu, table), not in the page. */
function hasOwnScroller(view: EditorView) {
  const root = view.dom.ownerDocument.documentElement
  for (
    let el = view.dom.parentElement;
    el && el !== root && el !== view.dom.ownerDocument.body;
    el = el.parentElement
  ) {
    const style = getComputedStyle(el)
    if (/^(fixed|sticky)$/.test(style.position)) return true
    if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight) return true
    // A wide table scrolls sideways: only ProseMirror's own reveal brings an off-screen column into view
    if (/(auto|scroll)/.test(style.overflowX) && el.scrollWidth > el.clientWidth) return true
  }
  return false
}

/**
 * The bottom edge of the sticky and fixed chrome at the top of the page (top banner, nav, page-tab strip): text
 * scrolled under it is hidden, not visible. A sticky layer only counts once it is stuck: until then it sits in the
 * flow and covers nothing. Read at call time, so a resized or wrapped header is always current.
 */
export function canvasTopInset(doc: Document) {
  let inset = parseFloat(getComputedStyle(doc.documentElement).getPropertyValue('--notch-top-banner-height')) || 0
  doc.querySelectorAll('[data-scroll-chrome]:not([data-scroll-chrome="bottom"])').forEach(el => {
    const rect = el.getBoundingClientRect()
    const style = getComputedStyle(el)
    const stuck = style.position === 'fixed' || rect.top <= (parseFloat(style.top) || 0) + 0.5
    if (rect.height > 0 && stuck) inset = Math.max(inset, rect.bottom)
  })
  return inset
}

/**
 * How much of the bottom of the viewport a fixed overlay (the snackbar stack) covers right now: a caret scrolled in
 * under it is hidden, though inside the viewport.
 */
export function canvasBottomInset(doc: Document, viewportHeight: number) {
  let inset = 0
  doc.querySelectorAll('[data-scroll-chrome="bottom"]').forEach(el => {
    const rect = el.getBoundingClientRect()
    if (rect.height > 0) inset = Math.max(inset, viewportHeight - rect.top)
  })
  return inset
}

/** Bottom edge of what the user can see: the visual viewport follows pinch zoom and the on-screen keyboard. */
export function visibleViewportBottom(win: Window) {
  const visual = win.visualViewport
  return visual ? visual.offsetTop + visual.height : win.innerHeight
}

/**
 * `handleScrollToSelection` prop: ProseMirror calls it after typing, Enter, key-made selections and programmatic
 * focus; returning true replaces its edge-only scroll, which does not know about the sticky header. Editors with
 * their own scroller fall back to ProseMirror.
 */
export function handleScrollToSelection(view: EditorView): boolean {
  // A throw here aborts the dispatch that called us (TipTap skips its update events), so fall back to ProseMirror.
  try {
    return scrollCaretIntoView(view)
  } catch (error) {
    console.error('[caret-scroll] scroll failed', error)
    return false
  }
}

function scrollCaretIntoView(view: EditorView): boolean {
  if (view.state.selection instanceof NodeSelection || hasOwnScroller(view)) return false
  const win = view.dom.ownerDocument.defaultView
  if (!win) return false
  const caret = view.coordsAtPos(view.state.selection.head, 1)
  const root = win.document.documentElement
  const target = caretRevealScrollTarget({
    scrollY: win.scrollY,
    caretTop: caret.top,
    caretBottom: caret.bottom,
    viewportHeight: visibleViewportBottom(win),
    maxScroll: root.scrollHeight - root.clientHeight,
    topInset: canvasTopInset(win.document),
    bottomInset: canvasBottomInset(win.document, win.innerHeight)
  })
  if (target !== null) win.scrollTo({ top: target, behavior: 'auto' })
  return true
}
